import { useState } from 'react';
import { Link } from 'react-router-dom';
import { familyInvitationService } from '../../lib/family-invitation-service';
import { Users, ArrowLeft, UserCheck, Mail } from 'lucide-react';

export default function JoinFamily() {
  const [step, setStep] = useState('code'); // 'code', 'preview', 'signup', 'confirmation'
  const [inviteCode, setInviteCode] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [userDetails, setUserDetails] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCheckInvitation = async (e) => {
    e.preventDefault();
    
    if (!inviteCode.trim()) {
      setError('Please enter an invitation code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const invitationData = await familyInvitationService.getInvitationByCode(inviteCode.toUpperCase());
      setInvitation(invitationData);
      setStep('preview');
    } catch (error) {
      console.error('Error checking invitation:', error);
      setError(error.message || 'Invalid invitation code');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();

    if (userDetails.password !== userDetails.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (userDetails.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await familyInvitationService.acceptInvitation(inviteCode, userDetails);
      
      // Success! Show email confirmation step
      console.log('Account created successfully:', result);
      setStep('confirmation');
      
    } catch (error) {
      console.error('Error creating account:', error);
      setError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  if (step === 'code') {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-stone-50">
        <div className="bg-neutral-50/95 backdrop-blur-sm rounded-3xl border border-neutral-50/50 shadow-xl p-12 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-gradient-to-r from-indigo-500 to-violet-600">
              <Users className="w-8 h-8 text-neutral-50" />
            </div>
            <h1 className="text-3xl font-semibold text-stone-800">Join a Family</h1>
            <p className="text-stone-600 mt-2">Enter your invitation code to get started</p>
          </div>

          <form onSubmit={handleCheckInvitation} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Invitation Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-neutral-50 text-center text-2xl font-mono tracking-wider"
                placeholder="ABC12345"
                maxLength={8}
                required
              />
              <p className="text-xs text-stone-500 mt-1">
                Enter the 8-character code you received
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 bg-gradient-to-r from-indigo-500 to-violet-600 text-neutral-50 font-semibold rounded-2xl hover:from-indigo-600 hover:to-violet-700 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Checking...' : 'Check Invitation'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-stone-600 hover:text-stone-800 text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'preview') {
    const age = calculateAge(invitation.date_of_birth);
    
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-stone-50">
        <div className="bg-neutral-50/95 backdrop-blur-sm rounded-3xl border border-neutral-50/50 shadow-xl p-12 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-gradient-to-r from-indigo-700 to-emerald_emerald-600">
              <UserCheck className="w-8 h-8 text-neutral-50" />
            </div>
            <h1 className="text-3xl font-semibold text-stone-800">You're Invited!</h1>
            <p className="text-stone-600 mt-2">Ready to join the family planner?</p>
          </div>

          <div className="bg-stone-50 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-stone-800 mb-4">Invitation Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-stone-600">Family:</span>
                <span className="font-medium">{invitation.family_name || 'Summer Planner'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-600">Your name:</span>
                <span className="font-medium">{invitation.invitee_name}</span>
              </div>
              {age && (
                <div className="flex justify-between">
                  <span className="text-stone-600">Age:</span>
                  <span className="font-medium">{age} years old</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-stone-600">Invited by:</span>
                <span className="font-medium">{invitation.family_members?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-600">Role:</span>
                <span className="font-medium capitalize">{invitation.role}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep('code')}
              className="flex-1 py-3 px-4 border border-stone-300 text-stone-700 rounded-xl hover:bg-stone-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep('signup')}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-indigo-500 to-violet-600 text-neutral-50 rounded-xl hover:from-indigo-600 hover:to-violet-700 transition-all"
            >
              Create Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'confirmation') {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-stone-50">
        <div className="bg-neutral-50/95 backdrop-blur-sm rounded-3xl border border-neutral-50/50 shadow-xl p-12 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-gradient-to-r from-teal-500 to-emerald-600">
              <Mail className="w-8 h-8 text-neutral-50" />
            </div>
            <h1 className="text-3xl font-semibold text-stone-800">Check Your Email!</h1>
            <p className="text-stone-600 mt-2">Almost there - just one more step</p>
          </div>

          <div className="bg-teal-50 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-teal-900 mb-3">Email Confirmation Required</h3>
            <p className="text-sm text-teal-800 mb-4">
              We've sent a confirmation email to <strong>{userDetails.email}</strong>
            </p>
            <p className="text-sm text-teal-700">
              Please check your inbox and click the confirmation link to complete your account setup.
            </p>
          </div>

          <div className="bg-stone-50 rounded-2xl p-4 mb-6">
            <p className="text-xs text-stone-600 text-center">
              Don't see the email? Check your spam folder or contact your family organizer for help.
            </p>
          </div>

          <Link
            to="/"
            className="w-full inline-block py-3 px-4 bg-gradient-to-r from-indigo-500 to-violet-600 text-neutral-50 rounded-xl hover:from-indigo-600 hover:to-violet-700 transition-all text-center font-medium"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (step === 'signup') {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-stone-50">
        <div className="bg-neutral-50/95 backdrop-blur-sm rounded-3xl border border-neutral-50/50 shadow-xl p-12 max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-semibold text-stone-800">Create Your Account</h1>
            <p className="text-stone-600 mt-2">
              Set up your login to join the family
            </p>
          </div>

          <form onSubmit={handleCreateAccount} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={userDetails.email}
                onChange={(e) => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-neutral-50"
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={userDetails.password}
                onChange={(e) => setUserDetails(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-neutral-50"
                placeholder="Choose a secure password"
                minLength={6}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={userDetails.confirmPassword}
                onChange={(e) => setUserDetails(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-neutral-50"
                placeholder="Confirm your password"
                minLength={6}
                required
              />
            </div>

            {/* Email confirmation notice */}
            <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
              <p className="text-sm text-azure-800">
                <strong>Note:</strong> After creating your account, you'll need to check your email and click the confirmation link before you can sign in.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setStep('preview')}
                className="flex-1 py-3 px-4 border border-stone-300 text-stone-700 rounded-xl hover:bg-stone-50 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-indigo-500 to-violet-600 text-neutral-50 rounded-xl hover:from-indigo-600 hover:to-violet-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}